import { ClientMemberRole } from '@maru44/huntre-utils/src/models/clientMember'
import { type DevicePosition, IsTrappingStatus } from '@maru44/huntre-utils/src/models/device'
import { type DeviceMessage, DeviceMessageKind } from '@maru44/huntre-utils/src/models/deviceMessage'
import { Box, Button, IconButton, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useContext, useState } from 'react'
import { IoOpenOutline, IoTrashSharp } from 'react-icons/io5'
import { useParams } from 'react-router-dom'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { IconPopover } from 'src/components/atoms/IconPopover'
import { StatusChip } from 'src/components/atoms/StatusChip'
import { DeleteClientDeviceDialog } from 'src/components/dialogs/device/DeleteClientDeviceDialog'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { useClientDevice } from 'src/hooks/clientDevice/useClientDevice'
import { useClientDeviceMessages } from 'src/hooks/clientDevice/useClientDeviceMessages'
import { useClientDeviceMemos } from 'src/hooks/clientDeviceMemo/useClientDeviceMemos'
import { ClientDeviceDetail } from 'src/pages/ClientDevice/ClientDeviceDetail'
import { formatTimestamp } from 'src/utils/firebase'
import { getPositionWithUpdatedAt } from 'src/utils/position'
import { ErrorPage } from '../ErrorPage'
import { KumapPostDialog } from './KumapPostDialog'
import { Memos } from './Memos'
import { DevicePositionContainer } from './Position'

export const ClientDevice = () => {
  const { id } = useParams()
  const { client, clientMembers, currentMember } = useContext(ClientContext)
  const { getClientDeviceWithDevice } = useClientDevice(id ?? '', client?.id ?? '')
  const { listMessagesForClient } = useClientDeviceMessages(id ?? '', getClientDeviceWithDevice.data?.clientDevice.createdAt)
  const { listDeviceMemos } = useClientDeviceMemos(client!.id, id ?? '')

  const [openDelete, setOpenDelete] = useState(false)

  return (
    <Box>
      <Breads breadIds={['home', 'device']} />
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">{getClientDeviceWithDevice.data?.clientDevice.deviceId ?? 'デバイス詳細'}</Typography>
          {currentMember?.role === ClientMemberRole.Admin && (
            <IconButton onClick={() => setOpenDelete(true)}>
              <IoTrashSharp />
            </IconButton>
          )}
        </Box>
        {getClientDeviceWithDevice.data && (
          <ClientDeviceDetail clientId={client?.id ?? ''} data={getClientDeviceWithDevice.data} mutate={getClientDeviceWithDevice.mutate} />
        )}
        {getClientDeviceWithDevice.error && <ErrorPage exception={getClientDeviceWithDevice.error} />}
      </Stack>
      {getClientDeviceWithDevice.data && (
        <>
          <Stack
            spacing={2}
            sx={{
              mt: 8,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">メモ</Typography>
              <IconPopover icon="?">
                <Typography>通知に送る設定をしているメモもしくは最新のメモが通知の際に送付されます。</Typography>
              </IconPopover>
            </Box>
            <Memos
              currentMember={currentMember}
              clientId={client?.id ?? ''}
              deviceId={id ?? ''}
              clientDevice={getClientDeviceWithDevice.data.clientDevice}
              members={clientMembers ?? []}
              memos={listDeviceMemos.data}
              mutateMemos={listDeviceMemos.mutate}
              mutateDevice={getClientDeviceWithDevice.mutate}
            />
          </Stack>
          <Stack
            spacing={2}
            sx={{
              mt: 8,
            }}
          >
            <Typography variant="h6">位置情報</Typography>
            <DevicePositionContainer
              device={getClientDeviceWithDevice.data.device}
              clientDevice={getClientDeviceWithDevice.data.clientDevice}
              clientId={client?.id ?? ''}
            />
          </Stack>
          <Stack
            spacing={2}
            sx={{
              mt: 8,
            }}
          >
            <Typography variant="h6">通信履歴</Typography>
            {listMessagesForClient.data && (
              <MessageList
                data={listMessagesForClient.data}
                clientId={client?.id ?? ''}
                deviceId={id ?? ''}
                deviceFallbackPosition={getPositionWithUpdatedAt(getClientDeviceWithDevice.data.device, getClientDeviceWithDevice.data.clientDevice).position}
                mutate={listMessagesForClient.mutate}
              />
            )}
          </Stack>
          <DeleteClientDeviceDialog
            clientId={client!.id}
            clientDevice={getClientDeviceWithDevice.data.clientDevice}
            open={openDelete}
            handleClose={() => setOpenDelete(false)}
          />
        </>
      )}
    </Box>
  )
}

type MessageListProps = {
  data: DeviceMessage[]
  clientId: string
  deviceId: string
  deviceFallbackPosition: DevicePosition | null
  mutate: () => void
}

const MessageList = ({ data, clientId, deviceId, deviceFallbackPosition, mutate }: MessageListProps) => {
  return (
    <TableContainer component={Paper} elevation={6}>
      <Table sx={{ minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle1">種別</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">作動状況</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">通信日時</Typography>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((v) => (
            <MessageRow key={v.id} data={v} clientId={clientId} deviceId={deviceId} deviceFallbackPosition={deviceFallbackPosition} mutate={mutate} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

type MessageRowProps = {
  data: DeviceMessage
  clientId: string
  deviceId: string
  deviceFallbackPosition: DevicePosition | null
  mutate: () => void
}

const MessageRow = ({ data, clientId, deviceId, deviceFallbackPosition, mutate }: MessageRowProps) => {
  const status = trappingStatus(data.kind, data.isTrapping)
  const [openKumap, setOpenKumap] = useState(false)
  return (
    <TableRow>
      <TableCell component="th" scope="row">
        {messageKindText(data.kind, data.wakeupCause)}
      </TableCell>
      <TableCell>{status && <StatusChip status={status} />}</TableCell>
      <TableCell>
        <Typography>{formatTimestamp(data.createdAt)}</Typography>
      </TableCell>
      <TableCell align="right">
        {data.kumapPointId ? (
          <Link
            href={`${import.meta.env.VITE_KUMAP_BASE_URL}/?id=${data.kumapPointId}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            くまっぷで開く <IoOpenOutline />
          </Link>
        ) : (
          data.isTrapping === true && (
            <>
              <Button size="small" variant="outlined" onClick={() => setOpenKumap(true)}>
                くまっぷに報告
              </Button>
              {openKumap && (
                <KumapPostDialog
                  open={openKumap}
                  onClose={() => setOpenKumap(false)}
                  clientId={clientId}
                  deviceId={deviceId}
                  msg={data}
                  deviceFallbackPosition={deviceFallbackPosition}
                  onPosted={mutate}
                />
              )}
            </>
          )
        )}
      </TableCell>
    </TableRow>
  )
}

const messageKindText = (kind: DeviceMessageKind, cause: string) => {
  switch (kind) {
    case DeviceMessageKind.SETUP:
      return '電源on'
    case DeviceMessageKind.TRAPPING_STATUS:
      if (cause === 'ESP_SLEEP_WAKEUP_EXT0' || cause === 'ESP_SLEEP_WAKEUP_EXT1') {
        return 'トリガー (磁石)'
      }
      return '定期通信'
    case DeviceMessageKind.MEASURE_VOLTAGE:
      return ''
  }
}

const trappingStatus = (kind: DeviceMessageKind, isTrapping: boolean | undefined): IsTrappingStatus | undefined => {
  if (kind === DeviceMessageKind.TRAPPING_STATUS) {
    if (isTrapping) {
      return IsTrappingStatus.Trapping
    }
    return IsTrappingStatus.NotTrapping
  }
}
